import React, { useEffect, useState } from 'react';
import axios from 'axios';
import cn from 'classnames';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { GlobalToken } from 'antd';

import { EPInput, EulaPage } from '@/comp';
import { IdPAuthenticationType } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';
import { EPButton } from '@/components';
import EcoplantLogo from '@/comp/common/icons/EcoplantLogo';
import { AuthenticationType } from '@/store/SiteSurveyForm/constants';

const SsoLoginPage = observer(() => {
  const { userStore } = useStores();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { from } = location.state || { from: { pathname: '/' } };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    if (userStore.hasSignedEula) {
      const redirectPath = localStorage.getItem('redirectAfterLogin') || from.pathname || '/home';
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath, { replace: true });
    }
  }, [isSubmitting, userStore.hasSignedEula]);

  useEffect(() => {
    const errorMessage = userStore.samlErrorResponse || userStore.ssoLoginFailureResponse;
    setErrorText(errorMessage);
  }, [userStore.samlErrorResponse, userStore.ssoLoginFailureResponse]);

  const onSubmit = async e => {
    e.preventDefault();
    userStore.setAuthenticationType(AuthenticationType.SSO);
    setIsSubmitting(true);

    try {
      const resp = await axios.get(`${import.meta.env.VITE_GET_SSO_LOGIN_URL}${email}`);

      const { authenticationType, loginUrl = '' } = resp.data;

      switch (authenticationType) {
        case IdPAuthenticationType.Azure:
        case IdPAuthenticationType.Okta:
          window.location.assign(`${loginUrl}&redirect_uri=${import.meta.env.VITE_WEB_URL}/login/sso`);
          break;
        default:
          setErrorText(t('SSOLoginNotEnabled'));
          break;
      }
    } catch (err) {
      setErrorText(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');

    if (code && !userStore.hasUserLoggedIn) {
      userStore.setAuthenticationType(AuthenticationType.SSO);
      userStore.authenticateCurrentUser();
    }
  }, []);

  const formLogin = (
    <form className={cn('form', { 'has-warning': errorText })} onSubmit={onSubmit} autoComplete="off">
      <EPInput
        placeholder={t('Email Address')}
        value={email}
        name="email"
        disabled={isSubmitting}
        onChange={e => setEmail(e.target.value)}
      />
      <div className="error-text">{errorText}</div>
      <EPButton loading={isSubmitting} disabled={isSubmitting} type="primary" className="submit" htmlType="submit">
        {t('Login')}
      </EPButton>

      <div className="line-container">
        <hr className="line" />
        <span>{t('OR')}</span>
        <hr className="line" />
      </div>

      <div className="single-sing-in-link">
        <Link to={'/login'}>{t('Sign in without Single Sign On')}</Link>
      </div>
    </form>
  );

  return (
    <Container>
      {!userStore.hasUserLoggedIn ? (
        <section>
          <div className="form-container">
            <div className="logo-container">
              <EcoplantLogo />
            </div>

            <p className="single-sing-in">{t('Sign in with Single Sign On')}</p>
            <Routes>
              <Route path="/" element={formLogin} />
            </Routes>

            <div className="email-us">
              {t('For technical support email us at:')}
              <a target="_blank" href="mailto:support@ecoplant.co" rel="noreferrer">
                {' '}
                support@ecoplant.co
              </a>
            </div>
          </div>
        </section>
      ) : (
        <EulaPage />
      )}
      <footer>
        <span className="right-reserved">© {t('EcoPlant 2023. All right reserved.')}</span>
      </footer>
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ theme: { token } }) => token.colorBgBase};

  section {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;

    .form-container {
      text-align: center;
      width: 400px;

      .logo-container {
        display: flex;
        justify-content: center;
      }

      .single-sing-in {
        margin-top: ${({ theme: { token } }) => token.marginXXL}px;
        font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
        font-weight: normal;
      }

      .form {
        margin-top: ${({ theme: { token } }) => token.marginLG}px;
        text-align: center;

        .action-bar {
          display: flex;
          justify-content: space-between;
          margin-top: ${({ theme: { token } }) => token.marginXS}px;
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        }

        .error-text {
          margin-top: ${({ theme: { token } }) => token.marginXS}px;
          font-size: ${({ theme: { token } }) => token.fontSize}px;
          color: ${({ theme: { token } }) => token.colorError};
        }

        .single-sing-in-link {
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          font-weight: normal;
          margin-bottom: ${({ theme: { token } }) => token.marginXXL}px;

          a {
            color: ${({ theme: { token } }) => token.colorText};
            text-decoration: underline;

            &:hover {
              text-decoration: none;
            }
          }
        }

        .line-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: ${({ theme: { token } }) => token.marginXXL}px 0;

          span {
            margin: 0 ${({ theme: { token } }) => token.margin}px;
            font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          }
        }

        .line {
          width: 100%;
          height: 0;
          margin: 0;
          border-top: solid 1px ${({ theme: { token } }) => token.colorBorder};
        }

        &.has-warning {
          > .ant-input,
          > .ant-input-affix-wrapper {
            border-color: ${({ theme: { token } }) => token.colorError};
          }
        }

        button.submit {
          margin-top: ${({ theme: { token } }) => token.marginXL}px;
        }
      }

      .email-us {
        margin-top: ${({ theme: { token } }) => token.marginXXL}px;
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextDisabled};

        a {
          color: ${({ theme: { token } }) => token.colorPrimary};
        }
      }
    }
  }

  footer {
    text-align: center;
    margin-bottom: ${({ theme: { token } }) => token.marginXL}px;

    .right-reserved {
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      color: ${({ theme: { token } }) => token.colorTextDisabled};
    }
  }
`;

export default SsoLoginPage;
